import { ApplicationConfig, inject, provideAppInitializer, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection } from '@angular/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideRouter, withComponentInputBinding, withHashLocation } from '@angular/router';
import { Actions, ofType, provideEffects } from '@ngrx/effects';
import { provideStore, Store } from '@ngrx/store';

import { routes } from './app.routes';
import { AuthEffects } from './store/auth/auth.effects';
import { authReducer } from './store/auth/auth.reducer';
import { ErrorInterceptor, LoaderInterceptor } from './core/interceptors';
import { userReducer } from './store/user/user.reducer';
import { UserEffects } from './store/user/user.effects';
import { RefreshInterceptor } from './core/interceptors/refresh.interceptor';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { RefreshTokenFailure, RefreshTokenRequest, RefreshTokenSuccess } from './store/auth/auth.actions';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { catchError, of, take } from 'rxjs';
import { channelReducer } from './store/channel/channel.reducer';
import { ChannelEffects } from './store/channel/channel.effects';
import { chatReducer } from './store/chat/chat.reducer';
import { webSocketReducer } from './store/websocket/websocket.reducer';
import { WebSocketEffects } from './store/websocket/websocket.effects';
import { ChatEffects } from './store/chat/chat.effects';
import { meetingReducer } from './store/meeting/meeting.reducer';
import { MeetingEffects } from './store/meeting/meeting.effects';
import { organizationReducer } from './store/organization/organization.reducer';
import { OrganizationEffects } from './store/organization/organization.effects';
import { bugReportReducer } from './store/bug-report/bug-report.reducer';
import { BugReportEffects } from './store/bug-report/bug-report.effects';
import { calendarReducer } from './store/calendar/calendar.reducer';
import { CalendarEffects } from './store/calendar/calendar.effects';
import { calendarIntegrationReducer } from './store/calendar-integration/calendar-integration.reducer';
import { CalendarIntegrationEffects } from './store/calendar-integration/calendar-integration.effects';
import { taskReducer } from './store/task/task.reducer';
import { TaskEffects } from './store/task/task.effects';
import { todoReducer } from './store/todo/todo.reducer';
import { TodoEffects } from './store/todo/todo.effects';
import { platformDashboardReducer } from './store/platform-dashboard/platform-dashboard.reducer';
import { PlatformDashboardEffects } from './store/platform-dashboard/platform-dashboard.effects';
import { taskReferenceReducer } from './store/task-reference/task-reference.reducer';
import { TaskReferenceEffects } from './store/task-reference/task-reference.effects';

function isPublicAuthBypassRoute(currentHash: string): boolean {
  return currentHash.startsWith('#/auth/reset-password')
    || currentHash.startsWith('#/auth/set-password')
    || currentHash.startsWith('#/sso/login')
    || currentHash.startsWith('#/calendar/join-token/')
    || currentHash.startsWith('#/call/');
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideHttpClient(),
    provideStore({auth: authReducer, user: userReducer, channel: channelReducer, chat: chatReducer, webSocket:webSocketReducer, meeting: meetingReducer, organization: organizationReducer, bugReport: bugReportReducer, calendar: calendarReducer, calendarIntegration: calendarIntegrationReducer, task: taskReducer, todo: todoReducer, platformDashboard: platformDashboardReducer, taskReference: taskReferenceReducer}),
    provideEffects(AuthEffects, UserEffects, ChannelEffects, ChatEffects, WebSocketEffects, MeetingEffects, OrganizationEffects, BugReportEffects, CalendarEffects, CalendarIntegrationEffects, TaskEffects, TodoEffects, PlatformDashboardEffects, TaskReferenceEffects),
    provideRouter(routes, withComponentInputBinding(),withHashLocation()),
    provideHttpClient(withInterceptorsFromDi()),
    {
        provide:HTTP_INTERCEPTORS,
        useClass:LoaderInterceptor,
        multi:true
    },
    {
        provide:HTTP_INTERCEPTORS,
        useClass:ErrorInterceptor,
        multi:true
    },
    {
        provide:HTTP_INTERCEPTORS,
        useClass:AuthInterceptor,
        multi:true
    },
    {
        provide:HTTP_INTERCEPTORS,
        useClass:RefreshInterceptor,
        multi:true
    },
    // To check if the user has a valid refresh token on app initialization and refresh the access token if valid. This will help in keeping the user logged in even after the access token expires as long as the refresh token is valid.
    provideAppInitializer(() => {
      const store = inject(Store);
      const actions$ = inject(Actions);
      const currentHash = window.location.hash || '#/';
      const isAuthTokenRoute = isPublicAuthBypassRoute(currentHash);

      if (isAuthTokenRoute) {
        return Promise.resolve();
      }

      store.dispatch(RefreshTokenRequest());
      // redirect to home page if refresh successful, otherwise stay on login page
      return firstValueFrom(
        actions$.pipe(
          ofType(RefreshTokenSuccess,RefreshTokenFailure),
          take(1),
          catchError(() => of(null))
        )
      ).then((result) => {
        if (result && result.type === RefreshTokenSuccess.type) {
          const isRootRoute = currentHash === '#' || currentHash === '#/' || currentHash === '';
          const isAuthRoute = currentHash.startsWith('#/auth');

          if (isRootRoute || (isAuthRoute && !isAuthTokenRoute)) {
            window.location.hash = '/home/chat';
          }
        }
      });
    })
  ],
};
